import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { FilesType, FileType } from '@/app/types'

export type PhoneNumberFilesInnerState = {
  loading: boolean
  loadingItems: string[]
  error: Record<string, unknown>
  data: FilesType
}

export type PhoneNumberFilesState = {
  files: PhoneNumberFilesInnerState
}

const initialInner: PhoneNumberFilesInnerState = {
  loading: false,
  loadingItems: [],
  error: {},
  data: {},
}

const initialState: PhoneNumberFilesState = {
  files: initialInner,
}

export const phoneNumberFilesSlice = createSlice({
  name: 'phoneNumberFiles',
  initialState,
  reducers: {
    getFileRequest: (state, action: PayloadAction<{ id: string; file?: string }>) => {
      const { id } = action.payload
      return {
        ...state,
        files: {
          ...state.files,
          loading: true,
          loadingItems: state.files.loadingItems.includes(id)
            ? state.files.loadingItems
            : [...state.files.loadingItems, id],
        },
      }
    },
    getFileSuccess: (
      state,
      action: PayloadAction<{ id: string; filename: string; url?: string; title?: string; transcription?: string }>,
    ) => {
      const { id, filename } = action.payload
      const existing = state.files.data[filename] || {}
      const nextData = { ...state.files.data, [filename]: { ...existing, ...action.payload } }
      return {
        ...state,
        files: {
          ...state.files,
          loading: false,
          loadingItems: state.files.loadingItems.filter((x) => x !== id),
          data: nextData,
        },
      }
    },
    getFileFailure: (state, action: PayloadAction<{ id: string; status?: number; message?: string }>) => {
      const { id } = action.payload
      return {
        ...state,
        files: {
          ...state.files,
          loading: false,
          loadingItems: state.files.loadingItems.filter((x) => x !== id),
        },
      }
    },
    saveFileRequest: (state, action: PayloadAction<{ id: string }>) => {
      const { id } = action.payload
      return {
        ...state,
        files: {
          ...state.files,
          loading: true,
          loadingItems: state.files.loadingItems.includes(id)
            ? state.files.loadingItems
            : [...state.files.loadingItems, id],
        },
      }
    },
    saveFileSuccess: (state, action: PayloadAction<FileType & { id: string }>) => {
      const { id, filename } = action.payload
      const nextData = { ...state.files.data, [filename]: action.payload }
      return {
        ...state,
        files: {
          ...state.files,
          loading: false,
          loadingItems: state.files.loadingItems.filter((x) => x !== id),
          data: nextData,
        },
      }
    },
    saveFileFailure: (state, action: PayloadAction<{ id: string; status?: number; message?: string }>) => {
      const { id } = action.payload
      return {
        ...state,
        files: {
          ...state.files,
          loading: false,
          loadingItems: state.files.loadingItems.filter((x) => x !== id),
        },
      }
    },
    resetFiles: (state, action: PayloadAction<{ id?: string } | undefined>) => {
      const id = action?.payload?.id
      let nextData = state.files.data
      if (id) {
        const current = { ...state.files.data } as any
        delete current[id]
        nextData = current
      } else {
        nextData = {}
      }
      return {
        ...state,
        files: {
          ...state.files,
          loading: false,
          data: nextData,
          loadingItems: [],
        },
      }
    },
    setPhoneNumberFiles: (state, action: PayloadAction<{ files: FilesType }>) => ({
      ...state,
      files: {
        ...state.files,
        data: { ...state.files.data, ...action.payload.files },
      },
    }),
  },
  selectors: {
    selectPhoneNumberFilesState: (state) => state.files,
  },
})

const { actions, reducer, selectors } = phoneNumberFilesSlice

export const {
  getFileRequest,
  getFileSuccess,
  getFileFailure,
  resetFiles,
  saveFileRequest,
  saveFileSuccess,
  saveFileFailure,
  setPhoneNumberFiles,
} = actions

export const { selectPhoneNumberFilesState } = selectors

export default reducer
